import { useEffect, useMemo, useState } from 'react'
import { useAppStore } from '../../store/app'
import { fetchOrganizationMembers, memberGovernanceSetup } from '../../api/ws'
import { SectionHeader, Group, TappableRow } from '../account/AccountComponents'
import '../account/AccountStyles.css'

// Member-onboarding (docs/onboarding.md §5.1). Owner-only. When a new person
// joins, the owner is prompted ONCE to choose what they can do — a human-trust
// decision, not an LLM proposal. Each choice writes through the existing,
// owner-preserving axis-B/C controls server-side. Renders nothing when there is
// no one to set up (solo household, or every member already addressed).
export function MemberGovernanceSection() {
  const organizationID = useAppStore((s) => s.organizationID)
  const members = useAppStore((s) => s.organizationMembers)
  const [busyID, setBusyID] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  // The members list isn't loaded on this screen by default; fetch it so the
  // prompt can appear without opening the debug members sheet.
  useEffect(() => {
    if (organizationID) fetchOrganizationMembers(organizationID)
  }, [organizationID])

  const pending = useMemo(
    () =>
      members.filter(
        (m) =>
          m.organization_id === organizationID &&
          m.needs_governance_setup &&
          !dismissed.has(m.human_user_id),
      ),
    [members, organizationID, dismissed],
  )

  if (!organizationID || pending.length === 0) return null

  async function choose(humanUserID: string, opts: { approve?: boolean; delegate?: boolean }) {
    if (busyID) return
    setBusyID(humanUserID)
    setError(null)
    const r = await memberGovernanceSetup({ organizationID: organizationID!, humanUserID, ...opts })
    setBusyID(null)
    if (r.error) {
      setError(r.error)
      return
    }
    setDismissed((prev) => new Set(prev).add(humanUserID)) // optimistic
  }

  return (
    <>
      <SectionHeader>New members</SectionHeader>
      <p className="about__hint">
        Decide what each new person can do. By default they can use the household
        but can't approve requests or change rules — you can grant that here, and
        change it any time in Approvers / Delegates.
      </p>
      {pending.map((m) => {
        const who = m.name || m.email || 'This person'
        const busy = busyID === m.human_user_id
        return (
          <Group key={m.human_user_id}>
            <p className="about__hint" style={{ fontWeight: 600, margin: '4px 0' }}>
              {who} joined — what can they do?
            </p>
            <TappableRow label="Also approve requests" onClick={() => choose(m.human_user_id, { approve: true })}>
              {busy ? '…' : ''}
            </TappableRow>
            <TappableRow label="Help manage rules" onClick={() => choose(m.human_user_id, { delegate: true })}>
              {busy ? '…' : ''}
            </TappableRow>
            <TappableRow label="Just use the household" onClick={() => choose(m.human_user_id, {})}>
              {busy ? '…' : ''}
            </TappableRow>
          </Group>
        )
      })}
      {error && (
        <p className="about__hint" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
    </>
  )
}
